/* eslint-disable no-await-in-loop -- need to execute sequentially */
import type { EmitActionParams } from '@unifyapps/defs/hooks/useBlockAction';
import type { TriggerEventParams } from '@unifyapps/defs/hooks/useBlockEvents';
import type { Action } from '@unifyapps/defs/types/action';
import type { Event } from '@unifyapps/defs/types/event';
import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import type { BlockId } from '@unifyapps/defs/types/block';
import type { SyntheticEvent } from '@unifyapps/defs/types/syntheticEvent';
import { logError } from '../../utils/error-reporting/log';
import type { OnActionType } from '../components/ActionsProvider/context';
import { getEventsToTrigger } from './utils/getEventsToTrigger';
import { getActionContext } from './utils/getActionContext';

export const BLOCK_TARGET_ID = 'BASE_BLOCK';
class NoCodeEventHandler {
  // {
  //   '<targetId>': {
  //     <eventType> : [{
  //       ...event
  //     }]
  //   }
  // }
  private events: Record<string, Record<string, Event[] | undefined> | undefined>;
  private processing: Record<string, Record<string, boolean> | undefined>;
  private readonly eventQueues: Record<string, Record<string, TriggerEventParams[]>>;
  private doAction: OnActionType;
  private readonly evaluateConditionalOperator: (params: {
    conditionEvaluationObj: ConditionEvaluationObject | undefined;
    context: Record<string, unknown>;
  }) => boolean | undefined;
  private readonly getComputeContext: () => Record<string, unknown>;

  constructor({
    evaluateConditionalOperator,
    events,
    getComputeContext,
  }: {
    evaluateConditionalOperator: NoCodeEventHandler['evaluateConditionalOperator'];
    getComputeContext: NoCodeEventHandler['getComputeContext'];
    events: Event[];
  }) {
    this.events = {};
    this.eventQueues = {};
    this.processing = {};
    this.evaluateConditionalOperator = evaluateConditionalOperator;
    this.getComputeContext = getComputeContext;
    this.registerEvents(events);
    this.registerQueue();
  }

  registerQueue() {
    const allTargetIds = [...Object.keys(this.events), BLOCK_TARGET_ID];
    for (const targetId of allTargetIds) {
      this.registerQueueEventType(targetId);
    }
  }

  registerQueueEventType(targetId: string) {
    this.eventQueues[targetId] = {};
    const eventTypes = Object.keys(this.events[targetId] || {});
    for (const eventType of eventTypes) {
      this.eventQueues[targetId][eventType] = [];
    }
  }

  registerEvents(events: Event[]) {
    for (const event of events) {
      this.registerEvent(event);
    }
  }

  registerEvent(event: Event) {
    const eventType = event.eventType;
    const targetId = event.targetId || BLOCK_TARGET_ID;
    this.events = {
      ...this.events,
      [targetId]: {
        ...this.events[targetId],
        [eventType]: [...(this.events[targetId]?.[eventType] || []), event],
      },
    };
    this.setEventToProcessing(eventType, targetId, false);
  }

  getEventsByType(
    eventType: string,
    targetId: string,
    actionContext: EmitActionParams['actionContext'],
    domEvent?: SyntheticEvent,
  ) {
    const events = this.events[targetId]?.[eventType] || [];

    return getEventsToTrigger({
      events,
      actionContext,
      eventType,
      domEvent,
    });
  }

  setEventToProcessing(eventType: string, targetId: string, processing: boolean) {
    this.processing = {
      ...this.processing,
      [targetId]: {
        ...this.processing[targetId],
        [eventType]: processing,
      },
    };
  }

  triggerEvent = async (params: TriggerEventParams) => {
    const { eventType, doAction, actionContext, blockId, domEvent } = params;
    const targetId = params.targetId || BLOCK_TARGET_ID;
    const events = this.getEventsByType(eventType, targetId, actionContext, domEvent);
    if (!events.length) {
      // console.error(`Event ${eventType} not registered.`);
      return;
    }

    this.eventQueues[targetId][eventType].push(params);

    if (!this.processing[targetId]?.[eventType]) {
      await this.processEventQueue(targetId, eventType, doAction, blockId);
    }
  };

  private async processEventQueue(
    targetId: string,
    eventType: string,
    doAction: OnActionType,
    blockId?: BlockId,
  ) {
    try {
      this.setEventToProcessing(eventType, targetId, true);
      this.doAction = doAction;

      while (this.eventQueues[targetId][eventType].length > 0) {
        const params = this.eventQueues[targetId][eventType].shift();
        if (params) await this.executeEvent(params);
      }
    } catch (error) {
      logError(error);
    } finally {
      this.setEventToProcessing(eventType, targetId, false);

      // Check if there are any new events in the queue
      if (this.eventQueues[targetId][eventType].length > 0) {
        await this.processEventQueue(targetId, eventType, doAction);
      }
    }
  }

  private async executeEvent(params: TriggerEventParams) {
    const { eventType, actionContext, domEvent } = params;
    const targetId = params.targetId || BLOCK_TARGET_ID;
    const events = this.getEventsByType(eventType, targetId, actionContext, domEvent);

    for (const event of events) {
      const { action } = event;

      await this.executeAction({
        action,
        actionContext,
        domEvent,
        blockId: params.blockId,
      });
    }
  }

  async executeActions(actions: Action[], emitActionParams: Partial<EmitActionParams>) {
    for (const action of actions) {
      await this.executeAction({
        ...emitActionParams,
        action,
      });
    }
  }

  async executeAction(params: EmitActionParams) {
    const { action, actionContext } = params;
    try {
      const computedContext = this.getComputeContext();

      const shouldRun = this.evaluateConditionalOperator({
        conditionEvaluationObj: action.runCondition,
        context: getActionContext(computedContext, actionContext),
      });

      if (shouldRun === false) {
        console.debug(
          'action run condition(determined by filters set in only run when field) not met, skipping event',
        );
        return;
      }

      if (typeof action.delayDuration === 'number') {
        await new Promise((resolve) => {
          setTimeout(resolve, action.delayDuration);
        });
      }

      const computeContext = this.getComputeContext();

      await this.doAction({
        ...params,
        actionContext: getActionContext(computeContext, actionContext),
      });

      if (action.onSuccessActions) {
        await this.executeActions(action.onSuccessActions, {
          actionContext: params.actionContext,
          domEvent: params.domEvent,
        });
      }
    } catch (error) {
      console.error(`Error executing action ${params.action.actionType}:`, error);
      if (params.action.onErrorActions) {
        await this.executeActions(params.action.onErrorActions, {
          actionContext: params.actionContext,
          domEvent: params.domEvent,
        });
      }
    }
  }

  getIsProcessingEvent = (targetId: string) => {
    return Object.keys(this.processing[targetId] || {}).some(
      (eventType) => this.processing[targetId]?.[eventType],
    );
  };
}

export default NoCodeEventHandler;
